/**
 * LevelUp — "Subiu de nível!" em tela cheia, standalone.
 * Fonte: handoffAnuncios/PROMPT-claude-code.md § 2 e a tela 2 de
 * html/Monetizacao.dc.html; motion e sons de handoff/05-motion.md.
 *
 * Roda sozinho depois de lição, mapa, treino e refeição — não depende do
 * CheckinCelebration. Quem chama decide QUANDO mostrar; aqui só se conta a
 * história:
 *
 *   t=0     VITA com o punho (pop 700ms spring) + haptic success
 *   t=500   "NÍVEL" + número (sobe) + som level-up; se pulou mais de um
 *           nível, o número conta de nivelAntes+1 até nivel
 *   t=900   título e subtítulo + confete (2,4s, primary/accent/success/info)
 *   CTA     "Bora!" → onClose
 *
 * `novaFaixa`: true (a faixa sai do nível) ou o nome da faixa em string.
 * Mostra o emblema pequeno com "Nova faixa: {nome}" e troca a luz do fundo
 * pelo tier.glow da faixa nova.
 *
 * Fecha pelo CTA, pelo Esc e pelo voltar do Android (pilha de overlays do
 * app, senão o voltar fecharia a tela de baixo e deixaria esta flutuando).
 * Portal no body: um ancestral com transform prenderia o `fixed`.
 */

import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import LevelBadge from '../../design/components/LevelBadge/LevelBadge';
import { faixaDoNivel } from '../../design/components/LevelBadge/faixas';
import Confetto from '../../components/ui/Confetto';
import useSoundEffect from '../../hooks/useSoundEffect';
import useOverlayScrollLock from '../../hooks/useOverlayScrollLock';
import haptics from '../../design/feedback/haptics';
import { formatarNumero } from '../../i18n/formatos';
import { registrarOffcanvas, removerOffcanvas } from '../../utils/offcanvasStack';
import levelSound from '../../Assets/sounds/level-up.mp3';
import popSound from '../../Assets/sounds/confete-pop.mp3';
import vitaPunho from '../../Assets/images/vita/vita-levelup-punho.webp';
import s from './LevelUp.module.scss';

/** Quando o número entra (ms) — casa com o atraso do `.nivel` no módulo. */
const ATRASO_NUMERO = 500;
/** Quando o título entra e o confete estoura. */
const ATRASO_CONFETE = 900;
/** Quanto tempo o confete fica na tela. */
const DURACAO_CONFETE = 2400;

/** Cores do confete: tokens resolvidos no próprio overlay (tema escuro). */
const TOKENS_CONFETE = [
  '--lv-color-primary',
  '--lv-color-accent',
  '--lv-color-success',
  '--lv-color-info',
];

const reduzido = () =>
  typeof window !== 'undefined' &&
  !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/** Verso do papel: a mesma cor mais escura, para o giro aparecer. */
function escurecer(cor, fator = 0.7) {
  const m = /^#([0-9a-f]{6})$/i.exec(cor);
  if (!m) return cor;
  const n = parseInt(m[1], 16);
  const c = (x) => Math.round(x * fator);
  return `rgb(${c(n >> 16)}, ${c((n >> 8) & 255)}, ${c(n & 255)})`;
}

function coresDoConfete(elemento) {
  try {
    const estilo = getComputedStyle(elemento);
    const cores = TOKENS_CONFETE.map((nome) => estilo.getPropertyValue(nome).trim()).filter(Boolean);
    return cores.map((front) => ({ front, back: escurecer(front) }));
  } catch {
    return [];
  }
}

export default function LevelUp({ nivel, nivelAntes, novaFaixa = false, onClose }) {
  const { t } = useTranslation('gamificacao');
  const { play } = useSoundEffect();
  const idTitulo = useId();

  const alvo = Math.max(1, Math.round(Number(nivel) || 1));
  const antes = Number(nivelAntes);
  // Subiu vários de uma vez: conta a partir do primeiro nível novo.
  const inicioContagem = Number.isFinite(antes) && antes < alvo - 1 ? Math.max(1, Math.round(antes) + 1) : alvo;
  const [exibido, setExibido] = useState(() => (reduzido() ? alvo : inicioContagem));

  const faixa = faixaDoNivel(alvo);
  const nomeFaixa = typeof novaFaixa === 'string' && novaFaixa ? novaFaixa : faixa.nome;
  const mostrarFaixa = !!novaFaixa;

  const raiz = useRef(null);
  const vita = useRef(null);
  const canvas = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const fechado = useRef(false);

  useOverlayScrollLock(true);

  const fechar = useCallback(() => {
    if (fechado.current) return;
    fechado.current = true;
    haptics.light();
    onCloseRef.current?.();
  }, []);

  // Voltar do Android e Esc.
  useEffect(() => {
    const id = registrarOffcanvas(fechar);
    const aoTeclar = (e) => {
      if (e.key === 'Escape') fechar();
    };
    window.addEventListener('keydown', aoTeclar);
    return () => {
      window.removeEventListener('keydown', aoTeclar);
      removerOffcanvas(id);
    };
  }, [fechar]);

  // Sequência: haptic → número + som → confete.
  useEffect(() => {
    const timers = [];
    const frames = [];
    const depois = (ms, fn) => timers.push(setTimeout(fn, ms));
    const quadro = (fn) => frames.push(requestAnimationFrame(fn));
    const semMovimento = reduzido();

    haptics.success();

    depois(ATRASO_NUMERO, () => {
      play(levelSound, { volume: 0.85 });
      if (semMovimento || inicioContagem >= alvo) {
        setExibido(alvo);
        return;
      }
      const passos = alvo - inicioContagem;
      const duracao = Math.min(900, 160 * passos);
      let inicio = null;
      const contar = (ts) => {
        inicio ??= ts;
        const p = Math.min(1, (ts - inicio) / duracao);
        const suave = 1 - Math.pow(1 - p, 3);
        setExibido(inicioContagem + Math.round(passos * suave));
        if (p < 1) quadro(contar);
      };
      quadro(contar);
    });

    if (!semMovimento) {
      depois(ATRASO_CONFETE, () => {
        const tela = canvas.current;
        const ctx = tela?.getContext('2d');
        if (!ctx) return;
        play(popSound, { volume: 0.55 });
        tela.width = window.innerWidth;
        tela.height = window.innerHeight;

        const caixa = vita.current?.getBoundingClientRect();
        const cx = caixa ? caixa.left + caixa.width / 2 : tela.width / 2;
        const cy = caixa ? caixa.top + caixa.height / 2 : tela.height / 3;
        const cores = coresDoConfete(raiz.current);
        const novo = () => {
          const p = new Confetto(cx, cy);
          if (cores.length) p.color = cores[Math.floor(Math.random() * cores.length)];
          return p;
        };

        let particulas = Array.from({ length: 80 }, novo);
        depois(200, () => particulas.push(...Array.from({ length: 45 }, novo)));

        let inicio = null;
        const desenhar = (ts) => {
          inicio ??= ts;
          ctx.clearRect(0, 0, tela.width, tela.height);
          if (ts - inicio > DURACAO_CONFETE) return;
          particulas.forEach((p) => {
            p.draw(ctx);
            p.update();
          });
          particulas = particulas.filter((p) => p.y < tela.height + 60);
          if (particulas.length) quadro(desenhar);
        };
        quadro(desenhar);
      });
    }

    return () => {
      timers.forEach(clearTimeout);
      frames.forEach(cancelAnimationFrame);
    };
  }, [alvo, inicioContagem, play]);

  const estiloRaiz = mostrarFaixa ? { '--lu-brilho': `var(--lv-color-tier-${faixa.chave}-glow)` } : undefined;

  const conteudo = (
    <div
      ref={raiz}
      className={s.raiz}
      style={estiloRaiz}
      // A celebração é desenhada no escuro: no tema claro o CTA e o halo do
      // número perderiam o contraste. Os tokens mudam por [data-theme].
      data-theme="dark"
      role="dialog"
      aria-modal="true"
      aria-labelledby={idTitulo}
    >
      <canvas ref={canvas} className={s.confete} aria-hidden="true" data-lv-confetti="" />

      <div className={s.palco}>
        <img ref={vita} className={s.vita} src={vitaPunho} alt="" />

        <div className={s.nivel}>
          <span className={s.rotulo}>{t('level_up.rotulo')}</span>
          <span className={s.numero}>{formatarNumero(exibido)}</span>
        </div>

        {mostrarFaixa && (
          <span className={s.faixa}>
            <LevelBadge nivel={alvo} tamanho={28} luz="none" />
            <span>{t('level_up.nova_faixa_rotulo')}</span>
            <b>{nomeFaixa}</b>
          </span>
        )}

        <div className={s.textos}>
          <h2 id={idTitulo} className={s.titulo}>
            {t('level_up.titulo')}
          </h2>
          <p className={s.sub}>{t('level_up.sub', { nivel: formatarNumero(alvo) })}</p>
        </div>
      </div>

      <div className={s.rodape}>
        <button type="button" className={s.cta} onClick={fechar} autoFocus>
          {t('level_up.acao')}
        </button>
      </div>
    </div>
  );

  return typeof document === 'undefined' ? conteudo : createPortal(conteudo, document.body);
}
